import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

export interface GalleryImage {
    id: number;
    gallery_id: number;
    type: "image" | "embed";
    image_url: string | null;
    embed_url: string | null;
    display_order: number;
}

export interface AdminGalleryItem {
    id: number;
    title: string | null;
    album: string | null;
    description: string | null;
    image_url: string | null;
    is_active: boolean | number;
    display_order: number;
    images: GalleryImage[];
    created_at?: string;
    updated_at?: string;
}

// GET All Gallery Items
export const useAdminGalleries = () => {
    return useQuery<AdminGalleryItem[]>({
        queryKey: ["admin-galleries"],
        queryFn: async () => {
            const { data } = await axiosClient.get("/galleries", { withCredentials: true });
            return data;
        },
    });
};

// GET Single Gallery Item
export const useAdminGallery = (id: number | null) => {
    return useQuery<AdminGalleryItem>({
        queryKey: ["admin-gallery", id],
        queryFn: async () => {
            const { data } = await axiosClient.get(`/galleries/${id}`, { withCredentials: true });
            return data;
        },
        enabled: !!id,
    });
};

// CREATE Gallery Item
export const useCreateGallery = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: FormData) => {
            const { data } = await axiosClient.post("/galleries", payload, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-galleries"] });
        },
    });
};

// UPDATE Gallery Item
export const useUpdateGallery = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: FormData }) => {
            // Route update galleries adalah POST (bukan PUT) - JANGAN pakai _method=PUT,
            // nanti Laravel mencari route PUT yang tidak terdaftar ("Method Not Allowed").
            const { data } = await axiosClient.post(`/galleries/${id}`, payload, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-galleries"] });
            queryClient.invalidateQueries({ queryKey: ["admin-gallery"] });
        },
    });
};

// DELETE Gallery Item
export const useDeleteGallery = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`/galleries/${id}`, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-galleries"] });
        },
    });
};
